import React, { useEffect, useRef, useState } from 'react';
import { api, BASE_URL } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/common/Modal';
import QuestionForm from '../components/admin/QuestionForm';
import type { Question, Serie } from '../types';

const TYPE_LABELS: Record<string, string> = {
  text: 'Texte',
  buzzer: 'Buzzer',
  multiple_choice: 'QCM',
};

interface QuestionCardProps {
  q: Question;
  i: number;
  total: number;
  series: Serie[];
  onEdit: () => void;
  onDelete: () => void;
  onMove: (index: number, dir: -1 | 1) => void;
}

function QuestionCard({ q, i, total, series, onEdit, onDelete, onMove }: QuestionCardProps) {
  const serie = series.find((s) => s.id === q.serieId);
  return (
    <div className="question-card">
      <div className="question-card-order">
        <button className="btn-icon" onClick={() => onMove(i, -1)} disabled={i === 0}>↑</button>
        <span>{i + 1}</span>
        <button className="btn-icon" onClick={() => onMove(i, 1)} disabled={i === total - 1}>↓</button>
      </div>
      <div className="question-card-body">
        <div className="question-card-title">{q.title}</div>
        {(q.songTitle || q.artist) && (
          <div className="question-card-artist">
            {q.songTitle}{q.songTitle && q.artist ? ' — ' : ''}{q.artist}
          </div>
        )}
        <div className="question-card-meta">
          <span className="badge">{TYPE_LABELS[q.answerType]}</span>
          <span className="badge">{q.maxPoints} pts</span>
          <span className="badge">{Math.round(q.durationMs / 1000)}s</span>
          {q.dynamicScoring && <span className="badge badge-accent">⏱ Dynamique</span>}
          {q.audioFile && <span className="badge badge-success">🎵 Audio</span>}
          {serie && <span className="badge badge-serie">📚 {serie.name}</span>}
        </div>
      </div>
      <div className="question-card-actions">
        <button className="btn btn-sm btn-secondary" onClick={onEdit}>✏ Modifier</button>
        <button className="btn btn-sm btn-danger" onClick={onDelete}>🗑</button>
      </div>
    </div>
  );
}

export default function AdminQuestions() {
  const { token } = useAuth();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [series, setSeries] = useState<Serie[]>([]);
  const [musics, setMusics] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Question | 'new' | null>(null);
  const [error, setError] = useState('');
  const [filterSerie, setFilterSerie] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const importRef = useRef<HTMLInputElement>(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [qs, ss, ms] = await Promise.all([
        api.get<Question[]>('/api/questions'),
        api.get<Serie[]>('/api/series'),
        api.get<string[]>('/api/musics'),
      ]);
      setQuestions(qs);
      setSeries(ss);
      setMusics(ms);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteQuestion = async (q: Question) => {
    if (!window.confirm(`Supprimer "${q.title}" ?`)) return;
    try {
      await api.delete(`/api/questions/${q.id}`);
      await load();
    } catch (e: any) { setError(e.message); }
  };

  const move = async (index: number, dir: -1 | 1) => {
    const filtered = filteredQuestions;
    const target = index + dir;
    if (target < 0 || target >= filtered.length) return;
    const newList = [...questions];
    const gi = newList.indexOf(filtered[index]);
    const gt = newList.indexOf(filtered[target]);
    [newList[gi], newList[gt]] = [newList[gt], newList[gi]];
    setQuestions(newList);
    await api.patch('/api/questions/reorder', { ids: newList.map((q) => q.id) });
  };

  const handleExport = () => {
    fetch(`${BASE_URL}/api/questions/export`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'questions.json';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch(() => setError('Export échoué'));
  };

  const handleImportClick = () => importRef.current?.click();

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      await api.post('/api/questions/import', data);
      setError('');
      await load();
    } catch (err: any) {
      setError(`Import échoué: ${err.message}`);
    } finally {
      if (importRef.current) importRef.current.value = '';
    }
  };

  const filteredQuestions = questions.filter((q) => {
    if (filterSerie !== 'all') {
      if (filterSerie === 'none' && q.serieId !== null) return false;
      if (filterSerie !== 'none' && String(q.serieId) !== filterSerie) return false;
    }
    if (filterType !== 'all' && q.answerType !== filterType) return false;
    return true;
  });

  if (loading) return <div className="page-loading">Chargement...</div>;

  return (
    <div className="admin-page">
      <div className="page-header">
        <h2>Questions ({questions.length})</h2>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={handleExport}>⬇ Exporter JSON</button>
          <button className="btn btn-secondary" onClick={handleImportClick}>⬆ Importer JSON</button>
          <button className="btn btn-primary" onClick={() => setEditing('new')}>+ Nouvelle question</button>
          <input
            ref={importRef}
            type="file"
            accept=".json"
            style={{ display: 'none' }}
            onChange={handleImportFile}
          />
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Serie tabs */}
      <div className="serie-tabs">
        <button
          className={`serie-tab ${filterSerie === 'all' ? 'active' : ''}`}
          onClick={() => setFilterSerie('all')}
        >
          Toutes ({questions.length})
        </button>
        {series.map((s) => (
          <button
            key={s.id}
            className={`serie-tab ${filterSerie === String(s.id) ? 'active' : ''}`}
            onClick={() => setFilterSerie(String(s.id))}
          >
            {s.name} ({questions.filter((q) => q.serieId === s.id).length})
          </button>
        ))}
        <button
          className={`serie-tab tab-unassigned ${filterSerie === 'none' ? 'active' : ''}`}
          onClick={() => setFilterSerie('none')}
        >
          Sans série ({questions.filter((q) => !q.serieId).length})
        </button>
      </div>

      {/* Type filter + count */}
      <div className="filters-row">
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
          <option value="all">Tous les types</option>
          <option value="text">Texte libre</option>
          <option value="buzzer">Buzzer</option>
          <option value="multiple_choice">QCM</option>
        </select>
        <span className="filter-count">{filteredQuestions.length} question{filteredQuestions.length !== 1 ? 's' : ''}</span>
      </div>

      {filteredQuestions.length === 0 ? (
        <div className="empty-state">
          {questions.length === 0 ? 'Aucune question. Créez-en une !' : 'Aucune question ne correspond aux filtres.'}
        </div>
      ) : filterSerie === 'all' ? (
        /* Grouped view by serie */
        <>
          {[
            ...series
              .map((s) => ({
                id: s.id, name: s.name, color: '#2563eb',
                qs: filteredQuestions.filter((q) => q.serieId === s.id),
              }))
              .filter((g) => g.qs.length > 0),
            {
              id: null, name: 'Sans série', color: '#f59e0b',
              qs: filteredQuestions.filter((q) => !q.serieId),
            },
          ]
            .filter((g) => g.qs.length > 0)
            .map((group) => {
              return (
                <div key={group.id ?? 'unassigned'} className="questions-group">
                  <div className={`questions-group-header ${group.id === null ? 'group-unassigned' : ''}`}
                    style={{ borderLeftColor: group.color }}>
                    <span className="questions-group-title">{group.name}</span>
                    <span className="questions-group-count">{group.qs.length} question{group.qs.length !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="questions-list">
                    {group.qs.map((q) => {
                      const i = filteredQuestions.indexOf(q);
                      return <QuestionCard key={q.id} q={q} i={i} total={filteredQuestions.length} series={series} onEdit={() => setEditing(q)} onDelete={() => deleteQuestion(q)} onMove={move} />;
                    })}
                  </div>
                </div>
              );
            })}
        </>
      ) : (
        <div className="questions-list">
          {filteredQuestions.map((q, i) => (
            <QuestionCard key={q.id} q={q} i={i} total={filteredQuestions.length} series={series} onEdit={() => setEditing(q)} onDelete={() => deleteQuestion(q)} onMove={move} />
          ))}
        </div>
      )}

      {editing && (
        <Modal
          title={editing === 'new' ? 'Nouvelle question' : 'Modifier la question'}
          onClose={() => setEditing(null)}
          wide
        >
          <QuestionForm
            question={editing === 'new' ? undefined : editing}
            series={series}
            availableMusics={musics}
            onSave={() => { setEditing(null); load(); }}
            onCancel={() => setEditing(null)}
          />
        </Modal>
      )}
    </div>
  );
}
